<script lang="ts" setup>
import { Plus, Trash01, Pencil02, X } from "utruckin-icons";
import { ref } from "vue";
import { onMounted } from "vue";
import { useOrganizationStore } from "@/features/organization/organization.store";
import {
  INewOrganization,
  IOrganization,
} from "@/features/organization/organization.types";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import { DateTime } from "luxon";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import { useNotification } from "@/utils/notification";
import router from "@/router";

const organizationStore = useOrganizationStore();
const { pushNotification } = useNotification();

const loading = ref<boolean>(false);
const organizations = ref<IOrganization[]>([]);

const modalItem = ref<any>();
const modalLoading = ref<boolean>(false);
const isModalVisible = ref<boolean>(false);
const modalType = ref<"create" | "edit">("create");

const getList = async () => {
  await organizationStore.getOrganizationList(loading, (res: any) => {
    if (res) {
      organizations.value = res;
    }
  });
};

const openModal = (type: "create" | "edit", item?: any) => {
  if (type == "create") {
    modalItem.value = {
      name: "",
      slug: "",
      billing_email: "",
      status: "active",
    };
    modalType.value = "create";
  } else {
    modalItem.value = { ...item };
    modalType.value = "edit";
  }
  modalLoading.value = false;
  isModalVisible.value = true;
};

const closeModal = () => {
  modalItem.value = {};
  isModalVisible.value = false;
  modalLoading.value = false;
};

const saveModal = async () => {
  if (modalType.value == "create") {
    if (!modalItem.value.name) {
      return;
    }

    await organizationStore.createOrganization(
      modalLoading,
      {
        billing_email: modalItem.value.billing_email,
        name: modalItem.value.name,
        slug: modalItem.value.name.toLowerCase().replace(/\s+/g, "_"),
        status: "active",
      },
      async (res: any) => {
        if (res) {
          pushNotification("Successfully created", "success");
          closeModal();
          await getList();
        }
      }
    );
  } else {
    if (!modalItem.value.name) {
      return;
    }
    await organizationStore.updateOrganization(
      modalLoading,
      {
        billing_email: modalItem.value.billing_email,
        name: modalItem.value.name,
        slug: modalItem.value.name.toLowerCase().replace(/\s+/g, "_"),
        status: "active",
      },
      modalItem.value.id,
      async (res: any) => {
        if (res) {
          pushNotification("Successfully saved", "success");
          closeModal();
          await getList();
        }
      }
    );
  }
};

function datepickerToUtcDateOnly(d: any) {
  return d
    ? DateTime.fromISO(d, {
        zone: "utc",
      })
        .setZone("America/New_York")
        .toFormat("MMM d, HH:mm")
    : "-";
}

const rowClick = (item: any, index: number) => {
  localStorage.setItem("organizationId", item.id);
  if (item.id) {
    router.push(`/all-tasks`);
  }
};

const deleteItem = async (item: any) => {
  const payload: INewOrganization = {
    name: item.name,
    billing_email: item.billing_email,
    slug: item.slug,
    status: "in_active",
  };

  await organizationStore.updateOrganization(
    loading,
    payload,
    item.id,
    async (res: any) => {
      if (res) {
        pushNotification("Successfully deleted", "success");
        await getList();
      }
    }
  );
};

onMounted(async () => {
  await getList();
});
</script>

<template>
  <div class="v-organizations">
    <div class="header">
      <div class="header__title">All organization</div>
      <div class="header__stats">
        <CommonButton
          type="primary"
          label="Add organization"
          prepend
          :prependIcon="Plus"
          @click="openModal('create')"
        />
      </div>
    </div>

    <div class="body">
      <CommonTable
        :headers="organizationStore.organizationHeaders"
        :loading="loading"
        :items="organizations"
        @row-click="rowClick"
      >
        <template #id="{ index }">
          <span>{{ index + 1 }}</span>
        </template>
        <template #created_time="{ item }">
          <span>{{ datepickerToUtcDateOnly(item.created_time) || "-" }}</span>
        </template>
        <template #updated_time="{ item }">
          <span>{{ datepickerToUtcDateOnly(item.updated_time) || "-" }}</span>
        </template>
        <template #billing_email="{ item }">
          <span>{{ item.billing_email || "-" }}</span>
        </template>
        <template #status="{ item }">
          <div class="status" :class="item.status ? '' : 'inactive'">
            {{ item.status ? "active" : "inactive" }}
          </div>
        </template>
        <template #actions="{ item }">
          <div class="action">
            <div
              class="action__item"
              v-tooltip="{
                content: 'Edit',
                html: true,
              }"
              @click="openModal('edit', item)"
            >
              <Pencil02 :size="18" />
            </div>
            <div
              class="action__item"
              v-tooltip="{
                content: 'Delete',
                html: true,
              }"
              @click="deleteItem(item)"
            >
              <Trash01 :size="18" />
            </div>
          </div>
        </template>
      </CommonTable>
    </div>

    <CommonModal
      v-model="isModalVisible"
      :title="modalType == 'create' ? 'Add organization' : 'Edit organization'"
      @close="closeModal"
    >
      <template v-if="modalItem">
        <div class="info">
          <CommonInput v-model="modalItem.name" label="Name" />
        </div>
        <div class="info">
          <CommonInput v-model="modalItem.billing_email" label="Email" />
        </div>
      </template>

      <template #footer>
        <div class="description__footer">
          <CommonButton type="outline" label="Cancel" @clicked="closeModal" />
          <CommonButton type="primary" label="Save" @clicked="saveModal" />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style lang="scss" scoped>
.v-organizations {
  display: flex;
  flex-direction: column;
  padding: var(--base-space-24);
  height: 100vh;
  background: var(--color-system-background);

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__title {
      font-weight: 600;
      font-size: 20px;
      line-height: 26px;
      letter-spacing: -2%;
    }
  }

  .status {
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-secondary-system-background);
    color: var(--color-system-green);
    display: inline-block;
    padding: 2px 8px;

    &.inactive {
      background: var(--color-red-lighter);
      color: var(--color-system-red);
    }
  }

  .action {
    display: flex;
    align-items: center;
    gap: 8px;

    &__item {
      width: 32px;
      height: 32px;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-secondary-system-background);
    }
  }
}

.info {
  margin-bottom: 16px;
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}
</style>
